import React, { useEffect, useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { ViewportOverlay } from '../Overlay/ViewportOverlay';

export interface ColourPickerModalProps {
  open: boolean;
  value: string;
  onClose: () => void;
  onSave: (colour: string) => void;
}

/** WCAG relative luminance. */
function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const to = (i: number) => {
    const c = parseInt(h.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * to(0) + 0.7152 * to(2) + 0.0722 * to(4);
}

/** Contrast of white text on this colour. */
export function contrastWithWhite(hex: string): number {
  return 1.05 / (luminance(hex) + 0.05);
}

/**
 * The brand colour is used for a header bar, links and buttons that all carry
 * white text, so a shade white cannot sit on is refused rather than offered and
 * left to look wrong. 4.5:1 is the WCAG AA threshold for normal text.
 */
const MIN_CONTRAST = 4.5;

/** A spread wide enough to brand with, without being a hex box (§9.1 card 02). */
const SWATCHES = [
  '#4C5BD4', '#3B6FD4', '#2E6BAF', '#1F7A56', '#2E7D62', '#20968C',
  '#6B4FD0', '#5B3F8C', '#4A3B7A', '#9B3D6E', '#BE4440', '#A9833A',
  '#8A6415', '#5A6270', '#242936', '#7FB2F0', '#8FD6C0', '#F0C987',
];

/**
 * The brand-colour picker (spec 15 §12). A small modal, explicit save.
 *
 * There is no hex input, and that is the point: a hex box accepts a shade white
 * text cannot sit on, and then the header, the links and the buttons all read
 * badly with nothing to warn anybody. Here the failing shades are shown and
 * disabled with the reason.
 */
export function ColourPickerModal({ open, value, onClose, onSave }: ColourPickerModalProps) {
  const [picked, setPicked] = useState(value);
  useEffect(() => {
    if (open) setPicked(value);
  }, [open, value]);

  const usable = SWATCHES.filter((s) => contrastWithWhite(s) >= MIN_CONTRAST);
  const refused = SWATCHES.filter((s) => contrastWithWhite(s) < MIN_CONTRAST);

  return (
    <ViewportOverlay open={open} onClose={onClose}>
      <div
        onClick={(e) => { e.stopPropagation(); }}
        className="flex w-[min(520px,calc(100vw-32px))] flex-col overflow-hidden rounded-[18px] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[0_18px_48px_rgba(36,41,54,.18)]"
      >
        <div className="flex items-start justify-between gap-3 border-b border-[var(--border-default)] px-6 py-5">
          <div>
            <h2 className="m-0 text-[17px] font-bold tracking-[-0.02em] text-[var(--text-primary)]">Brand colour</h2>
            <p className="m-0 mt-1 max-w-[380px] text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
              One colour. It themes the treatment page, the phasing offer, the payment page and the booking page.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] border border-[var(--border-default)] text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]"
          >
            <Icon kind="x" size={14} stroke={2} />
          </button>
        </div>

        <div className="px-6 py-5">
          {/* A live preview, because the colour is only ever seen carrying white text. */}
          <div className="overflow-hidden rounded-[12px] border border-[var(--border-default)]">
            <div className="px-4 py-3 text-[14px] font-bold text-white" style={{ background: picked }}>
              Alamo Heights Dental
            </div>
            <div className="flex items-center gap-3 px-4 py-3">
              <span
                className="rounded-[8px] px-3 py-1.5 text-[12.5px] font-semibold text-white"
                style={{ background: picked }}
              >
                Confirm this appointment
              </span>
              <span className="text-[12.5px] font-semibold" style={{ color: picked }}>
                A link looks like this
              </span>
            </div>
          </div>

          <div className="mt-5 text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
            White text sits cleanly on these
          </div>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {usable.map((s) => (
              <button
                key={s}
                type="button"
                aria-label={s}
                aria-pressed={picked === s}
                onClick={() => { setPicked(s); }}
                className={cn(
                  'flex h-11 w-11 items-center justify-center rounded-[10px] ring-offset-2 transition',
                  picked === s ? 'ring-2 ring-[var(--text-primary)]' : 'hover:ring-2 hover:ring-[var(--border-strong)]',
                )}
                style={{ background: s }}
              >
                {picked === s ? <Icon kind="check" size={15} stroke={2.6} color="#fff" /> : null}
              </button>
            ))}
          </div>

          {refused.length > 0 ? (
            <>
              <div className="mt-5 text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
                Refused — white text cannot sit on these
              </div>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {refused.map((s) => (
                  <span
                    key={s}
                    title={`${s} — contrast ${contrastWithWhite(s).toFixed(1)}:1 with white`}
                    className="relative flex h-11 w-11 cursor-not-allowed items-center justify-center rounded-[10px] opacity-60"
                    style={{ background: s }}
                  >
                    <Icon kind="x" size={14} stroke={2.6} color="#fff" />
                  </span>
                ))}
              </div>
              <p className="m-0 mt-2.5 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
                These are shown rather than hidden so nobody hunts for a shade that was quietly removed. The header,
                the buttons and the links all carry white text, so anything under{' '}
                <strong className="text-[var(--text-secondary)]">{MIN_CONTRAST}:1</strong> would be unreadable
                everywhere at once —{' '}
                <strong className="text-[var(--text-secondary)]">
                  which is the whole reason this is not a hex box.
                </strong>
              </p>
            </>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-4 border-t border-[var(--border-default)] bg-[var(--surface-sunken)] px-6 py-4">
          <span className="font-[var(--font-mono)] text-[11.5px] text-[var(--text-muted)]">
            {picked} · {contrastWithWhite(picked).toFixed(1)}:1
          </span>
          <span className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-2 text-[13.5px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-header)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => { onSave(picked); }}
              className="rounded-[10px] bg-[var(--brand-indigo)] px-4 py-2 text-[13.5px] font-semibold text-white"
            >
              Use this colour
            </button>
          </span>
        </div>
      </div>
    </ViewportOverlay>
  );
}
